import type { StripeElementLocale } from '@stripe/stripe-js';
import type { DateArg } from 'date-fns';
import { createContext, useContext } from 'react';
import type { FormatNumberOptions, IntlShape } from 'react-intl';

import type {
  AvailableLanguage,
  GenericLanguage,
} from '#src/i18n/useTranslations.ts';
import type { RoundingMode } from '#src/lib/wei.ts';

export type DisplayOptions = {
  hideSymbol?: boolean;
};

export interface IntlContext extends IntlShape {
  formatEth: (
    value: string | number,
    options?: FormatNumberOptions,
    displayOptions?: DisplayOptions
  ) => string;
  formatSol: (
    value: string | number,
    options?: FormatNumberOptions,
    displayOptions?: DisplayOptions
  ) => string;
  formatWei: (
    value: string | bigint,
    roundingMode?: RoundingMode,
    options?: FormatNumberOptions,
    displayOptions?: DisplayOptions
  ) => string;
  formatLamport: (
    value: string | bigint,
    roundingMode?: RoundingMode,
    options?: FormatNumberOptions,
    displayOptions?: DisplayOptions
  ) => string;
  formatCardBonus: (value: number) => string;
  formatDistanceToNow: (
    date: DateArg<Date>,
    options?: { addSuffix: boolean }
  ) => string;
  formatDistanceToNowStrict: (
    date: DateArg<Date>,
    options?: { addSuffix: boolean }
  ) => string;
  setLocale: (newLocale: string) => void;
  language: AvailableLanguage;
  genericLanguage: GenericLanguage;
  dir: 'rtl' | 'ltr';
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SUPPORTED_LOCALES = [
  'en-US',
  'de-DE',
  'es-ES',
  'fr-FR',
  'it-IT',
  'ru-RU',
  'tr-TR',
  'nl-NL',
  'pt-PT',
] as const;

export type LOCALE_KEY = (typeof SUPPORTED_LOCALES)[number];

export const localeConfig: {
  [index in GenericLanguage]: {
    date: () => Promise<any>;
    dir: 'rtl' | 'ltr';
    name: string;
  };
} = {
  en: {
    date: async () => import('date-fns/locale/en-US'),
    dir: 'ltr',
    name: 'English',
  },
  de: {
    date: async () => import('date-fns/locale/de'),
    dir: 'ltr',
    name: 'Deutsch',
  },
  es: {
    date: async () => import('date-fns/locale/es'),
    dir: 'ltr',
    name: 'Español',
  },
  fr: {
    date: async () => import('date-fns/locale/fr'),
    dir: 'ltr',
    name: 'Français',
  },
  it: {
    date: async () => import('date-fns/locale/it'),
    name: 'Italiano',
    dir: 'ltr',
  },
  ru: {
    date: async () => import('date-fns/locale/ru'),
    name: 'русский',
    dir: 'ltr',
  },
  tr: {
    date: async () => import('date-fns/locale/tr'),
    name: 'Türk',
    dir: 'ltr',
  },
  pt: {
    date: async () => import('date-fns/locale/pt'),
    name: 'Português',
    dir: 'ltr',
  },
  nl: {
    date: async () => import('date-fns/locale/nl'),
    name: 'Nederlands',
    dir: 'ltr',
  },
};

export const STRIPE_LOCALES: Record<GenericLanguage, StripeElementLocale> = {
  de: 'de',
  en: 'en',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  ru: 'ru',
  tr: 'tr',
  nl: 'nl',
  pt: 'pt',
} as const;

export const IntlContext = createContext<IntlContext | null>(null);

export const useIntlContext = () => useContext(IntlContext)!;
